import { useState } from 'react';
import { rankingToday, rankingWeek } from '@/mocks/home';
import SectionHeading from '@/pages/home/components/SectionHeading';

const trendIcon: Record<string, string> = {
  up: 'ri-arrow-up-line text-primary-600',
  down: 'ri-arrow-down-line text-secondary-600',
  flat: 'ri-subtract-line text-foreground-400',
};

export default function HotRanking() {
  const [range, setRange] = useState<'today' | 'week'>('today');
  const list = range === 'today' ? rankingToday : rankingWeek;

  return (
    <section id="ranking" className="bg-background-50">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-6 md:py-24">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            index="02"
            eyebrow="Heat Ranking"
            title="热度榜"
            desc="热度按讨论增量与补充线索数综合计算，每天零点重置，本周榜保留七天内的高点。"
          />
          <div className="mb-10 flex gap-1 rounded-full bg-background-200 p-1 md:mb-14">
            {[
              { key: 'today', label: '今日' },
              { key: 'week', label: '本周' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setRange(tab.key as 'today' | 'week')}
                className={
                  range === tab.key
                    ? 'whitespace-nowrap rounded-full bg-foreground-950 px-5 py-2 text-sm text-background-50'
                    : 'whitespace-nowrap rounded-full px-5 py-2 text-sm text-foreground-600 transition-colors hover:text-foreground-950'
                }
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.6fr_1fr] lg:gap-12">
          <ol className="border-t border-background-200">
            {list.map((item) => (
              <li key={item.id} className="border-b border-background-200 py-5 md:py-6">
                <div className="flex items-start gap-4 md:gap-6">
                  <span
                    className={
                      item.rank <= 3
                        ? 'font-heading text-2xl leading-none text-accent-600 md:text-3xl'
                        : 'font-heading text-2xl leading-none text-foreground-300 md:text-3xl'
                    }
                  >
                    {String(item.rank).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-background-200 px-2 py-0.5 font-label text-[11px] text-foreground-600">
                        {item.category}
                      </span>
                      <i className={`text-sm ${trendIcon[item.trend]}`} />
                    </div>
                    <h3 className="mt-2 font-heading text-base leading-snug text-foreground-950 md:text-lg">
                      {item.title}
                    </h3>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="h-1.5 w-full max-w-[220px] overflow-hidden rounded-full bg-background-200">
                        <div
                          className="h-full rounded-full bg-primary-500"
                          style={{ width: `${item.heat}%` }}
                        />
                      </div>
                      <span className="font-label text-xs text-foreground-500">{item.heat}</span>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          <aside className="h-fit rounded-lg border border-background-200 bg-background-100 p-6 md:p-7">
            <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
              <i className="ri-fire-line text-xl text-primary-600" />
              本周风向
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              榜单前三名的类别差异，比名次本身更能说明讨论正在往哪走。
            </p>
            <ul className="mt-6 space-y-5">
              <li className="flex gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />
                <p className="text-sm leading-relaxed text-foreground-700">
                  争议重心从「有没有」转向「什么时候」，话题位移明显。
                </p>
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                <p className="text-sm leading-relaxed text-foreground-700">
                  源头回溯类内容首次冲进前三，回看需求明显上升。
                </p>
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-secondary-500" />
                <p className="text-sm leading-relaxed text-foreground-700">
                  更正公告类条目热度回落，说明旧版本的传播已经基本停止。
                </p>
              </li>
            </ul>
            <a
              href="#timeline"
              className="mt-7 flex items-center justify-between rounded-md bg-foreground-950 px-4 py-3 text-sm text-background-50 transition-opacity hover:opacity-90"
            >
              按时间线重看首位事件
              <i className="ri-arrow-right-line text-base" />
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}